// src/views/InventarioForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../apiConfig";
import { OFICINAS_POR_MUNICIPIO } from "../catalogos/oficinasPorMunicipio";

export default function InventarioForm({ onSaved }) {
  // =======================
  // INPUT STYLES
  // =======================
  const inputBase =
    "w-full mt-1.5 px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 shadow-sm " +
    "focus:outline-none focus:ring-2 focus:ring-sky-500/80 focus:border-sky-400 placeholder-slate-400 transition";

  const selectBase =
    "w-full mt-1.5 px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 shadow-sm " +
    "focus:outline-none focus:ring-2 focus:ring-sky-500/80 focus:border-sky-400 transition";

  const textareaBase =
    "w-full mt-1.5 px-3.5 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 shadow-sm " +
    "focus:outline-none focus:ring-2 focus:ring-sky-500/80 focus:border-sky-400 transition";

  // =======================
  // TOAST SYSTEM
  // =======================
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };

  const cerrarToast = (id) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  // =======================
  // FORM STATE
  // =======================
  const [equipoOtro, setEquipoOtro] = useState("");
  const [marcaOtra, setMarcaOtra] = useState("");

  const [form, setForm] = useState({
    id: "",
    equipo: "",
    subtipoSoporte: "",
    marca: "",
    modelo: "",
    serie: "",
    estadoEquipo: "",
    fechaAdquisicion: "",
    proyecto: "",
    municipio: "",
    oficina: "",
    modulo: "",
    notas: "",
    estatusAsignacion: "STOCK",
  });

  const [nuevoID, setNuevoID] = useState("");
  const [oficinasDisponibles, setOficinasDisponibles] = useState([]);
  const [serieDuplicada, setSerieDuplicada] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // =======================
  // GENERATE ID
  // =======================
  const generarID = () => {
    const id = Math.floor(Math.random() * 900000) + 100000;
    setNuevoID(id);
    setForm((prev) => ({ ...prev, id }));
  };

  useEffect(() => {
    generarID();
  }, []);

  // =======================
  // HANDLE CHANGE
  // =======================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // =======================
  // VALIDATE DUPLICATE SERIAL
  // =======================
  const validarSerieDuplicada = async (serie) => {
    try {
      const res = await axios.get(`${API_BASE}/inventario/buscar?serie=${serie}`);
      return Array.isArray(res.data) && res.data.length > 0;
    } catch (error) {
      console.error("Error validando serie:", error);
      return false;
    }
  };

  useEffect(() => {
    if (!form.serie || form.serie.trim() === "") {
      setSerieDuplicada(false);
      return;
    }

    const check = async () => {
      const serieN = form.serie.trim().toUpperCase();
      const existe = await validarSerieDuplicada(serieN);
      setSerieDuplicada(existe);
    };

    check();
  }, [form.serie]);
  // =======================
  // RESET FORM
  // =======================
  const limpiarFormulario = () => {
    setForm({
      id: "",
      equipo: "",
      subtipoSoporte: "",
      marca: "",
      modelo: "",
      serie: "",
      estadoEquipo: "",
      fechaAdquisicion: "",
      proyecto: "",
      municipio: "",
      oficina: "",
      modulo: "",
      notas: "",
      estatusAsignacion: "STOCK",
    });

    setEquipoOtro("");
    setMarcaOtra("");
    setOficinasDisponibles([]);
    setSerieDuplicada(false);
    generarID();
  };

  // =======================
  // VALIDATION
  // =======================
  const handleSubmit = (e) => {
    e.preventDefault();

    // -------- CAMPOS OBLIGATORIOS BASE --------
    const obligatorios = [
      "equipo",
      "marca",
      "modelo",
      "estadoEquipo",
      "estatusAsignacion",
    ];

    // SUBTIPO DE SOPORTE
    if (form.equipo === "Soporte de Cámara") {
      obligatorios.push("subtipoSoporte");
    }

    // CUANDO ESTÁ ASIGNADO → PROYECTO ES OBLIGATORIO
    if (form.estatusAsignacion === "ASIGNADO") {
      obligatorios.push("proyecto", "municipio", "oficina", "modulo");
    }

    for (let campo of obligatorios) {
      if (!form[campo] || String(form[campo]).trim() === "") {
        showToast(`El campo "${campo}" es obligatorio.`, "warning");
        return;
      }
    }

    if (serieDuplicada) {
      showToast("El número de serie ya existe en el inventario.", "error");
      return;
    }

    setShowConfirm(true);
  };

  // =======================
  // SAVE
  // =======================
  const ejecutarGuardado = async () => {
    setIsSaving(true);

    const dataEnviar = {
      ...form,
      equipo: form.equipo === "Otro" ? equipoOtro : form.equipo,
      marca: form.marca === "OTRA" ? marcaOtra : form.marca,
      subtipoSoporte: form.subtipoSoporte || "",
      serie: form.serie ? form.serie.trim().toUpperCase() : "",
    };

    try {
      await axios.post(`${API_BASE}/inventario`, dataEnviar);
      showToast("Equipo guardado correctamente", "success");
      limpiarFormulario();
      onSaved && onSaved();
    } catch (error) {
      console.error("❌ ERROR AL GUARDAR:", error);
      showToast("Ocurrió un error inesperado al guardar.", "error");
    } finally {
      setIsSaving(false);
      setShowConfirm(false);
    }
  };

  // =======================
  // LISTA DE EQUIPOS
  // =======================
  const listaEquipos = [
    "Monitor",
    "Impresora",
    "CPU",
    "Escáner de Huellas",
    "Pad de Firma",
    "Terminal de Cobros",
    "Soporte de Cámara",
    "Cámara Profesional",
    "Micrófono",
    "Escáner de Documentos",
    "Lector Óptico",
    "Otro",
  ];

  // =======================
  // LISTA DE MARCAS
  // =======================
  const listaMarcas = [
    "WACOM",
    "SUPREMA INC",
    "XEROX",
    "SWIFTPRO",
    "CANON",
    "HP",
    "IDP",
    "NETPAY",
    "STEREN",
    "THALES",
    "OTRA",
  ];

  // =======================
  // RENDER (INICIO)
  // =======================
  return (
    <div className="w-full min-h-screen bg-slate-100 flex items-start justify-center py-10 px-4">
      <div className="w-full max-w-6xl bg-white rounded-3xl shadow-[0_20px_60px_rgba(15,23,42,0.15)] border border-slate-100 px-6 sm:px-10 py-8 sm:py-10">

        {/* ENCABEZADO */}
        <header className="mb-8 sm:mb-10 border-b border-slate-100 pb-5">
          <p className="text-xs font-semibold tracking-[0.28em] text-sky-500 uppercase mb-2">
            Inventario de equipos
          </p>
        </header>

        {/* FORMULARIO */}
        <form onSubmit={handleSubmit} className="space-y-8">

          {/* ============================
              SECCIÓN: DATOS DEL EQUIPO
          ============================ */}
          <section className="rounded-2xl bg-slate-50/70 border border-slate-100 px-5 py-5 sm:px-6 sm:py-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                  <rect x="4" y="4" width="16" height="12" rx="2" />
                  <path d="M8 20h8" />
                  <path d="M10 16v4" />
                  <path d="M14 16v4" />
                </svg>
              </span>

              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  Datos del equipo
                </h2>
                <p className="text-xs text-slate-500">
                  Identificación y características principales.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

              {/* ID */}
              <div>
                <label className="text-xs font-medium text-slate-600">ID</label>
                <input
                  disabled
                  value={nuevoID}
                  className={
                    inputBase +
                    " bg-slate-100 text-slate-700 border-slate-200 font-semibold"
                  }
                />
              </div>

              {/* EQUIPO */}
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Equipo <span className="text-rose-500">*</span>
                </label>

                <select
                  name="equipo"
                  value={form.equipo}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value !== "Soporte de Cámara") {
                      setForm((prev) => ({ ...prev, subtipoSoporte: "" }));
                    }
                  }}
                  className={selectBase}
                >
                  <option value="">Seleccione equipo</option>
                  {listaEquipos.map((eq) => (
                    <option key={eq} value={eq}>
                      {eq}
                    </option>
                  ))}
                </select>

                {/* SUBTIPO SOLO SI ES SOPORTE */}
                {form.equipo === "Soporte de Cámara" && (
                  <select
                    name="subtipoSoporte"
                    className={selectBase + " mt-3"}
                    value={form.subtipoSoporte}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, subtipoSoporte: e.target.value }))
                    }
                  >
                    <option value="">Seleccione tipo de soporte</option>
                    <option value="Brazo Articulado">Brazo Articulado</option>
                    <option value="Tripié">Tripié</option>
                  </select>
                )}

                {/* EQUIPO OTRO */}
                {form.equipo === "Otro" && (
                  <input
                    type="text"
                    placeholder="Especifique el equipo"
                    className={inputBase + " mt-3"}
                    value={equipoOtro}
                    onChange={(e) => setEquipoOtro(e.target.value)}
                  />
                )}
              </div>

              {/* MARCA */}
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Marca <span className="text-rose-500">*</span>
                </label>

                <select
                  name="marca"
                  value={form.marca}
                  onChange={handleChange}
                  className={selectBase}
                >
                  <option value="">Seleccione marca</option>
                  {listaMarcas.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>

                {form.marca === "OTRA" && (
                  <input
                    type="text"
                    placeholder="Especifique la marca"
                    className={inputBase + " mt-3"}
                    value={marcaOtra}
                    onChange={(e) => setMarcaOtra(e.target.value)}
                  />
                )}
              </div>

              {/* MODELO */}
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Modelo <span className="text-rose-500">*</span>
                </label>
                <input
                  name="modelo"
                  value={form.modelo}
                  onChange={handleChange}
                  className={inputBase}
                  placeholder="Ej. EliteBook 840 G8"
                />
              </div>

              {/* SERIE */}
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Número de serie
                </label>
                <input
                  name="serie"
                  placeholder="Ej. ABC12345"
                  value={form.serie}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      serie: e.target.value.toUpperCase().trim(),
                    })
                  }
                  className={
                    inputBase +
                    (serieDuplicada
                      ? " border-rose-400 bg-rose-50 text-rose-800"
                      : "")
                  }
                />

                {serieDuplicada && (
                  <p className="text-rose-500 text-[11px] mt-1.5 font-medium">
                    ❌ Esta serie ya existe en el inventario.
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* ============================
              SECCIÓN: ESTADO OPERATIVO
          ============================ */}
          <section className="rounded-2xl bg-slate-50/70 border border-slate-100 px-5 py-5 sm:px-6 sm:py-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                  <path d="M10.5 3h3l.75 2.5 2.25.75 1.75-1.75 2.1 2.1L19.5 9.5l.75 2.25L22.5 12v3l-2.25.75-.75 2.25 1.75 1.75-2.1 2.1L16.5 20.5l-2.25.75L13.5 24h-3l-.75-2.5L7.5 20.5 5.75 22.25 3.65 20.15 5.4 18.4 4.65 16.15 2.5 15v-3l2.15-.75L5.4 9 3.65 7.25 5.75 5.15 7.5 6.9l2.25-.75L10.5 3z" />
                  <circle cx="12" cy="12" r="3.25" fill="white" />
                </svg>
              </span>

              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  Estado operativo
                </h2>
                <p className="text-xs text-slate-500">
                  Define la condición funcional actual del equipo.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

              {/* ESTADO OPERATIVO */}
            
            <div>
              <label className="text-xs font-medium text-slate-600">
                Estado operativo <span className="text-rose-500">*</span>
              </label>
              <select
                name="estadoEquipo"
                value={form.estadoEquipo}
                onChange={handleChange}
                className={selectBase}
              >
                <option value="">Seleccione estado</option>
                <option value="NUEVO">NUEVO</option>
                <option value="BUENO">BUENO</option>
                <option value="REGULAR">REGULAR</option>
                <option value="MALO">MALO</option>
                <option value="EN REPARACIÓN">EN REPARACIÓN</option>
                <option value="NO OPERATIVO">NO OPERATIVO</option>
              </select>
            </div>

              {/* FECHA ADQUISICIÓN */}
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Fecha de adquisición
                </label>
                <input
                  type="date"
                  name="fechaAdquisicion"
                  value={form.fechaAdquisicion}
                  onChange={handleChange}
                  className={inputBase}
                />
              </div>
            </div>
          </section>

          {/* ============================
              SECCIÓN: UBICACIÓN / STOCK
          ============================ */}
          <section className="rounded-2xl bg-slate-50/70 border border-slate-100 px-5 py-5 sm:px-6 sm:py-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-purple-100 text-purple-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                  <path d="M12 21s-6-5.2-6-10.2A6 6 0 0 1 18 10.8C18 15.8 12 21 12 21z" />
                  <circle cx="12" cy="10.5" r="2.3" />
                </svg>
              </span>

              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  Ubicación / stock
                </h2>
                <p className="text-xs text-slate-500">
                  Define si el equipo está en stock o asignado a una oficina.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

              {/* ESTATUS ASIGNACIÓN */}
              <div className="md:col-span-3 lg:col-span-1">
                <label className="text-xs font-medium text-slate-600">
                  Estatus <span className="text-rose-500">*</span>
                </label>

                <div className="mt-2 flex flex-wrap gap-2">
                  {["STOCK", "ASIGNADO"].map((op) => (
                    <button
                      key={op}
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          estatusAsignacion: op,
                          municipio: op === "STOCK" ? "" : prev.municipio,
                          oficina: op === "STOCK" ? "" : prev.oficina,
                          proyecto: op === "STOCK" ? "" : prev.proyecto,
                          modulo: op === "STOCK" ? "" : prev.modulo,
                        }))
                      }
                      className={
                        "px-3.5 py-2 rounded-full text-xs font-semibold border transition shadow-sm " +
                        (form.estatusAsignacion === op
                          ? "bg-sky-500 text-white border-sky-400 shadow-sm"
                          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-100")
                      }
                    >
                      {op === "STOCK" ? "En stock (sin asignar)" : "Asignado a oficina"}
                    </button>
                  ))}
                </div>
              </div>

              {/* CAMPOS SOLO SI ESTÁ ASIGNADO */}
              {form.estatusAsignacion === "ASIGNADO" && (
                <>
                  {/* PROYECTO */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">
                      Proyecto <span className="text-rose-500">*</span>
                    </label>

                    <select
                      name="proyecto"
                      value={form.proyecto}
                      onChange={handleChange}
                      className={selectBase}
                    >
                      <option value="">Seleccione proyecto</option>
                      <option value="SYSNOT">SYSNOT</option>
                      <option value="TARJETA DE CIRCULACION">TARJETA DE CIRCULACION</option>
                      <option value="LICENCIAS">LICENCIAS</option>
                      <option value="PASAPORTES OME CHETUMAL">PASAPORTES OME CHETUMAL</option>
                    </select>
                  </div>

                  {/* MUNICIPIO */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">
                      Municipio <span className="text-rose-500">*</span>
                    </label>

                    <select
                      name="municipio"
                      value={form.municipio}
                      onChange={(e) => {
                        const mun = e.target.value;
                        const oficinas = OFICINAS_POR_MUNICIPIO[mun] || [];
                        setOficinasDisponibles(oficinas);

                        setForm((prev) => ({
                          ...prev,
                          municipio: mun,
                          oficina: oficinas.length ? oficinas[0] : "",
                        }));
                      }}
                      className={selectBase}
                    >
                      <option value="">Seleccione municipio</option>
                      {Object.keys(OFICINAS_POR_MUNICIPIO).map((mun) => (
                        <option key={mun} value={mun}>
                          {mun}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* OFICINA */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">
                      Oficina <span className="text-rose-500">*</span>
                    </label>

                    <select
                      name="oficina"
                      value={form.oficina}
                      onChange={handleChange}
                      className={selectBase}
                    >
                      <option value="">Seleccione oficina</option>
                      {oficinasDisponibles.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* MÓDULO */}
                  <div>
                    <label className="text-xs font-medium text-slate-600">
                      Módulo / Puesto <span className="text-rose-500">*</span>
                    </label>
                    <input
                      name="modulo"
                      value={form.modulo}
                      onChange={handleChange}
                      className={inputBase}
                      placeholder="Ej. Módulo 1, C2, Escritorio..."
                    />
                  </div>
                </>
              )}
            </div>
          </section>
          {/* ============================
              SECCIÓN: NOTAS
          ============================ */}
          <section className="rounded-2xl bg-slate-50/70 border border-slate-100 px-5 py-5 sm:px-6 sm:py-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                  <path d="M7 3h10a2 2 0 0 1 2 2v9.5L14.5 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                  <path d="M14 21v-4a2 2 0 0 1 2-2h4" />
                  <path d="M9 8h6" />
                  <path d="M9 12h3" />
                </svg>
              </span>

              <div>
                <h2 className="text-sm font-semibold text-slate-900">
                  Notas adicionales
                </h2>
                <p className="text-xs text-slate-500">
                  Observaciones, incidencias previas o comentarios relevantes.
                </p>
              </div>
            </div>

            <textarea
              name="notas"
              value={form.notas}
              onChange={handleChange}
              className={textareaBase + " h-28 resize-none"}
              placeholder="Ejemplo: Equipo asignado a proyecto piloto, ubicación temporal, incidencias previas, etc."
            />
          </section>

          {/* ============================
              BOTONES
          ============================ */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={limpiarFormulario}
              className="inline-flex justify-center items-center px-6 py-2.5 rounded-full border border-slate-300 text-sm font-semibold text-slate-600 bg-white hover:bg-slate-50 transition"
            >
              Limpiar formulario
            </button>

            <button
              type="submit"
              disabled={isSaving || serieDuplicada}
              className={`inline-flex items-center justify-center px-8 py-2.5 rounded-full text-sm font-semibold tracking-wide shadow-lg transition
                ${
                  serieDuplicada
                    ? "bg-slate-300 text-slate-600 cursor-not-allowed shadow-none"
                    : "bg-sky-500 hover:bg-sky-600 text-white"
                }`}
            >
              {serieDuplicada
                ? "Serie duplicada"
                : isSaving
                ? "Guardando..."
                : "Guardar equipo"}
            </button>
          </div>
        </form>

        {/* ========== TOASTS ========== */}
        <div className="toast-container">
          {toasts.map((t) => (
            <div key={t.id} className={`toast toast-${t.type}`}>
              <span>{t.message}</span>
              <span
                className="toast-close"
                onClick={() => cerrarToast(t.id)}
              >
                ×
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ============================
          MODAL CONFIRMACIÓN
      ============================ */}
      {showConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-[99998]">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Confirmar guardado
            </h3>

            <p className="text-sm text-slate-600 mb-6">
              ¿Confirmas que deseas guardar este equipo con la información capturada?
            </p>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-xl border border-slate-300 text-sm font-semibold text-slate-600 bg-white hover:bg-slate-50 transition"
              >
                Cancelar
              </button>

              <button
                type="button"
                disabled={isSaving}
                onClick={ejecutarGuardado}
                className="px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-sm font-semibold text-white transition disabled:opacity-75"
              >
                {isSaving ? "Guardando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================
   ESTILOS PREMIUM TOASTS
============================== */
const style = `
.toast-container {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateX(40px) translateY(10px); }
  to { opacity: 1; transform: translateX(0) translateY(0); }
}

.toast {
  min-width: 320px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 14px;
  font-size: 0.95rem;
  font-weight: 600;
  color: white;
  box-shadow: 0 18px 40px rgba(15,23,42,0.9);
  background: linear-gradient(135deg, #020617, #0f172a);
  border: 1px solid rgba(148,163,184,0.35);
  animation: slideIn 0.4s ease-out;
  backdrop-filter: blur(18px);
}

.toast-success { background: linear-gradient(135deg,#16a34a,#22c55e); border-color: rgba(74,222,128,0.7); }
.toast-error { background: linear-gradient(135deg,#dc2626,#ef4444); border-color: rgba(248,113,113,0.7); }
.toast-warning { background: linear-gradient(135deg,#f97316,#facc15); border-color: rgba(252,211,77,0.7); color:#111827; }
.toast-info { background: linear-gradient(135deg,#0ea5e9,#38bdf8); border-color: rgba(125,211,252,0.8); }

.toast-close {
  margin-left: auto;
  cursor: pointer;
  font-size: 20px;
  opacity: 0.9;
}
.toast-close:hover {
  opacity: 1;
}
`;

if (typeof document !== "undefined") {
  const existing = document.head.querySelector("style[data-inventario-toasts]");
  if (!existing) {
    const styleTag = document.createElement("style");
    styleTag.setAttribute("data-inventario-toasts", "true");
    styleTag.innerHTML = style;
    document.head.appendChild(styleTag);
  }
}
